import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Tabs, Tab, CircularProgress,
  Chip, Button, Alert, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  IconButton, useTheme,
} from "@mui/material";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  getProject, updateProject, deleteProject, listProjectRuns,
  getProjectDeployment,
  getAnalyzeSession, getDecisionOutput, getSessionAnalyses,
  downloadV2RunNotebook,
  getProjectDeploymentSpec, renderProjectLiveView, uploadProjectDeploymentCsv,
  extractProjectReportSections, refreshProjectReport, getProjectDashboardData,
} from "../utils/api";
import { getDomainProfile } from "../config/domainProfiles";
import { parseDecisionOutputPayload } from "../utils/decisionFormatters";
import BusinessDataSourcePanel from "../components/BusinessDataSourcePanel";
import BusinessRecommendation from "../components/BusinessRecommendation";
import StockSummaryGrid from "../components/StockSummaryGrid";
import StockFilterBar from "../components/StockFilterBar";
import ModelRankingGrid from "../components/ModelRankingGrid";
import { readStockPayload, readStockPacked } from "../utils/stockDashboardPayload";
import { legacySectionSkin } from "../components/liveGridStyles";
import ShareModal from "../components/ShareModal";
import ProjectHeaderStrip from "../components/ProjectHeaderStrip";
import DeploymentDetailsDrawer from "../components/DeploymentDetailsDrawer";
import DeploymentDialog from "../components/DeploymentDialog";
import DeploymentReviseDialog from "../components/DeploymentReviseDialog";
import ModelChat from "../components/ModelChat";
import AnalyzeChat from "./AnalyzeChat";
import { useImpersonation } from "../contexts/ImpersonationContext";

// ─── Edit project dialog ──────────────────────────────────────────────────────

function EditProjectDialog({ project, open, onClose, onSaved }) {
  const [name, setName] = useState(project.name || "");
  const [description, setDescription] = useState(project.description || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setName(project.name || "");
      setDescription(project.description || "");
      setError("");
    }
  }, [open, project]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError("");
    try {
      const patch = { name: name.trim(), description: description.trim() };
      await updateProject(project.project_id, patch);
      onSaved({ ...project, ...patch });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Edit project</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
        <TextField label="Project name" size="small" fullWidth autoFocus
          value={name} onChange={(e) => setName(e.target.value)} />
        <TextField label="Description (optional)" size="small" fullWidth multiline rows={2}
          value={description} onChange={(e) => setDescription(e.target.value)} />
        {error && <Typography variant="caption" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!name.trim() || saving}
          sx={{ textTransform: "none", boxShadow: "none" }}>
          {saving ? <CircularProgress size={16} color="inherit" /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Report tab ───────────────────────────────────────────────────────────────

function ReportTab({ project, projectId, canEdit, onProjectUpdated }) {
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(!!project.session_id);
  const [error, setError] = useState("");
  // When a report is ready, default to showing it. The user can flip to
  // the chat to iterate. When no report exists, the chat is the only
  // useful view.
  const [view, setView] = useState("report"); // "report" | "chat"
  // Initial-turn handoff state for the embedded chat: when the composer
  // creates a new session, we stash the user's typed message so the
  // embedded AnalyzeChat fires the streaming endpoint as soon as it mounts.
  const [pendingFirstMessage, setPendingFirstMessage] = useState(null);
  // Refresh re-queries the source data and re-renders server-side. Held in
  // local state so the iframe swaps without refetching the whole session.
  const [refreshing, setRefreshing] = useState(false);
  const [refreshed, setRefreshed] = useState(null); // { html, data_as_of }

  useEffect(() => {
    if (!project.session_id) { setSessionLoading(false); return; }
    getAnalyzeSession(project.session_id)
      .then(setSession)
      .catch((e) => {
        // Don't swallow this. A failed load left `session` null, which fell
        // through to the "no report yet" branch and rendered a chat composer —
        // so a permission or network failure looked like an empty project.
        setError(
          e?.response?.status === 403
            ? "You don't have access to this project's report."
            : e?.response?.data?.error || e?.message || "Could not load the report."
        );
      })
      .finally(() => setSessionLoading(false));
  }, [project.session_id]);

  const handleRefresh = async () => {
    setRefreshing(true);
    setError("");
    try {
      const res = await refreshProjectReport(projectId);
      setRefreshed({ html: res?.html || "", data_as_of: res?.data_as_of || null });
    } catch (e) {
      setError(
        e?.response?.data?.error || e?.message || "Could not refresh the report"
      );
    } finally {
      setRefreshing(false);
    }
  };

  // Session created via composer → no navigation; we update local project
  // state (which triggers re-render → falls into state 2 with chat inline)
  // and hand the user's typed message to the embedded chat to start
  // streaming immediately.
  const goToSession = async (result, opts = {}) => {
    await updateProject(projectId, { session_id: result.session_id });
    if (opts.firstUserMessage) setPendingFirstMessage(opts.firstUserMessage);
    if (typeof onProjectUpdated === "function") {
      onProjectUpdated({ ...project, session_id: result.session_id });
    }
    setView("chat");
  };

  if (sessionLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>;
  }

  // 30-min Calendly event for "we built a model, now let's productionize it"
  // conversations. Prefill with the project name so the seller walks in
  // already knowing which project the customer wants to deploy.
  const deploymentCallHref =
    `https://calendly.com/assertion-ai/deployment-call?a1=${encodeURIComponent(project.name || "this project")}`;

  // State 1: session has a report. Show iframe by default; user can flip
  // to "Chat" to continue refining. The chat is the SAME inline embedded
  // chat as state 2 — no page navigation either way.
  if (session?.report_html) {
    return (
      <Box>
        <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
          {canEdit && (
            <>
              <Button
                size="small"
                variant={view === "report" ? "contained" : "outlined"}
                onClick={() => setView("report")}
                sx={{ textTransform: "none", boxShadow: "none" }}
              >
                Report
              </Button>
              <Button
                size="small"
                variant={view === "chat" ? "contained" : "outlined"}
                onClick={() => setView("chat")}
                sx={{ textTransform: "none", boxShadow: "none" }}
              >
                Chat
              </Button>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          {view === "report" && canEdit && (
            <>
              {/* Data age, not render time — a fresh render of a stale source
                  table must not read as current. */}
              {(refreshed?.data_as_of || session.report_data_as_of) && (
                <Typography variant="caption" color="text.secondary">
                  Data as of{" "}
                  {(refreshed?.data_as_of || session.report_data_as_of).slice(0, 10)}
                </Typography>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={handleRefresh}
                disabled={refreshing}
                startIcon={refreshing ? <CircularProgress size={12} /> : null}
                sx={{ textTransform: "none", boxShadow: "none" }}
              >
                {refreshing ? "Refreshing…" : "Refresh data"}
              </Button>
            </>
          )}
          {view === "report" && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              href={deploymentCallHref}
              target="_blank"
              rel="noopener"
              sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none" }}
            >
              Book a deployment call
            </Button>
          )}
        </Box>

        {(!canEdit || view === "report") ? (
          <Box sx={{ position: "relative", borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider", mb: 2 }}>
            <iframe
              srcDoc={refreshed?.html || session.report_html}
              title="Project Report"
              style={{ width: "100%", height: 600, border: "none", display: "block" }}
              sandbox="allow-scripts allow-same-origin allow-downloads"
            />
            <Box sx={{ position: "absolute", top: 8, right: 8 }}>
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  // Navigate to the standalone AnalyzeReport route in a new tab.
                  // That route renders the same report HTML but preserves our
                  // React-controlled "Book a deployment call" button in its top
                  // bar. Previously we wrote raw HTML to a blank window, which
                  // stripped the deploy CTA entirely — full-screen had no
                  // working booking button.
                  const reportPath = projectId
                    ? `/projects/${projectId}/analyze/${project.session_id}/report`
                    : `/analyze/${project.session_id}/report`;
                  window.open(reportPath, "_blank", "noopener");
                }}
                sx={{ textTransform: "none", fontSize: 12, bgcolor: "background.paper" }}
              >
                Full screen
              </Button>
            </Box>
          </Box>
        ) : (
          <AnalyzeChat
            embedded
            sessionId={project.session_id}
            projectId={projectId}
          />
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      </Box>
    );
  }

  // State 2: session exists but no report yet — embed the chat directly.
  // No more standalone navigation; the chat lives inside the tab so the
  // user never leaves the project surface.
  if (project.session_id) {
    if (!canEdit) {
      return (
        <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
          <Typography variant="body2">
            Report not yet available — the project owner is still building it.
          </Typography>
        </Box>
      );
    }
    return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
        <AnalyzeChat
          embedded
          sessionId={project.session_id}
          projectId={projectId}
          initialUserMessage={pendingFirstMessage}
          onInitialMessageConsumed={() => setPendingFirstMessage(null)}
        />
      </Box>
    );
  }

  // State 3: no session yet. Owners get the chat itself in empty state
  // (composer is part of the chat surface); sharees see a placeholder.
  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {canEdit ? (
        <AnalyzeChat
          embedded
          projectId={projectId}
          // First send creates a session; instead of letting AnalyzeChat
          // navigate away, hand the result up so we update project state
          // in place and re-render the tab in state 2 (chat).
          onSessionCreated={async (result, opts = {}) => {
            await goToSession(result, opts);
          }}
        />
      ) : (
        <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
          <Typography variant="body2">
            Report not yet available — the project owner hasn't built it.
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── Evidence tab ─────────────────────────────────────────────────────────────
// The audit-trail view for a project. Show the full SQL, full Python code,
// and full outputs — chronologically, with enough detail that the project
// owner can re-do or verify any step. Findings live elsewhere (chat /
// Project overview); this tab is "show your work".

// Reusable monospace block with copy button + optional collapse toggle.
// `defaultOpen` controls initial state. Empty content renders nothing.
function CodeBlock({ label, content, language = "text", defaultOpen = false, maxHeight = 360 }) {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);
  if (!content) return null;
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex", alignItems: "center", gap: 0.5,
          cursor: "pointer", userSelect: "none",
          fontSize: 11, color: "text.secondary",
          py: 0.25,
        }}
      >
        <Typography variant="caption" sx={{
          fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
        }}>
          {label}
        </Typography>
        <Typography variant="caption" color="primary">{open ? "▴" : "▾"}</Typography>
        {open && (
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
            {copied && (
              <Typography variant="caption" color="success.main">Copied</Typography>
            )}
            <IconButton size="small" onClick={handleCopy} sx={{ opacity: 0.7 }}>
              <ContentCopyIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Box>
        )}
      </Box>
      {open && (
        <Box
          component="pre"
          data-lang={language}
          sx={{
            m: 0, p: 1.5, borderRadius: 1, bgcolor: "action.hover",
            fontSize: 12, fontFamily: "monospace", overflow: "auto",
            whiteSpace: "pre-wrap", wordBreak: "break-word",
            maxHeight, minHeight: 32,
          }}
        >
          {content}
        </Box>
      )}
    </Box>
  );
}

// ─── Folder-style Evidence helpers ────────────────────────────────────────────

// "Predictive Driver Analysis And Quantification" → "predictive_driver_analysis_and_quantification"
function _slugify(s) {
  return (s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60) || "untitled";
}

// "stage_distribution" + "sql", turn=5, taken={…} → unique filename
function _filenameFor(item, ext, taken) {
  let base = _slugify(item.label) || `item_${item.turn ?? 0}`;
  let name = `${base}.${ext}`;
  let i = 2;
  while (taken.has(name)) {
    name = `${base}_${i}.${ext}`;
    i += 1;
  }
  taken.add(name);
  return name;
}

// Stitch all files of one extension into a single text blob with separators
// and trigger a browser download. Cheap, no JSZip dependency.
function _downloadAll(extLabel, files) {
  if (!files.length) return;
  const banner = (f) => `${"=".repeat(70)}\n-- ${f.name}${f.turn != null ? `  (chat turn ${f.turn})` : ""}\n${"=".repeat(70)}\n`;
  const content = files.map((f) => banner(f) + f.content + "\n\n").join("\n");
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `evidence_${extLabel}.txt`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 250);
}

// One file row inside a folder. Click filename → expand inline code + output.
function EvidenceFile({ filename, turn, status, primaryContent, primaryLabel, secondaryContent, secondaryLabel, narration, failedReason }) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const isFailed = status === "failed";
  return (
    <Box sx={{
      px: 1.5, py: 0.75,
      borderTop: `1px solid ${theme.palette.divider}`,
      "&:hover": { bgcolor: theme.palette.action.hover },
      borderLeft: isFailed ? `3px solid ${theme.palette.error.main}` : "3px solid transparent",
    }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "flex", alignItems: "center", gap: 1,
          cursor: "pointer", userSelect: "none",
        }}
      >
        <Typography variant="caption" sx={{ color: "primary.main", width: 14 }}>
          {open ? "▾" : "▸"}
        </Typography>
        <Box sx={{ fontFamily: "monospace", fontSize: 13, color: isFailed ? "error.main" : "text.primary", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {filename}
        </Box>
        {turn != null && (
          <Typography variant="caption" color="text.disabled">turn {turn}</Typography>
        )}
        {isFailed && (
          <Chip label="failed" size="small" color="error" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
        )}
      </Box>
      {open && (
        <Box sx={{ pl: 3, pt: 0.5 }}>
          {narration && (
            <Typography variant="caption" color="text.secondary" sx={{
              display: "block", fontStyle: "italic", mb: 0.5,
            }}>
              {narration.slice(0, 240)}{narration.length > 240 ? "…" : ""}
            </Typography>
          )}
          {failedReason && (
            <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: 12 }}>
              {failedReason}
            </Alert>
          )}
          {primaryContent && (
            <CodeBlock label={primaryLabel || "Content"} content={primaryContent} defaultOpen={true} />
          )}
          {secondaryContent && (
            <CodeBlock label={secondaryLabel || "Output"} content={secondaryContent} defaultOpen={false} />
          )}
        </Box>
      )}
    </Box>
  );
}

// Folder section: header with name + count + download-all, then children below.
function EvidenceFolder({ name, hint, count, downloadFiles, downloadExt, defaultOpen = true, children }) {
  const theme = useTheme();
  const [open, setOpen] = useState(defaultOpen);
  if (!count) return null;
  return (
    <Box sx={{
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 1,
      bgcolor: "background.paper",
    }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          px: 1.5, py: 1,
          display: "flex", alignItems: "center", gap: 1,
          cursor: "pointer", userSelect: "none",
        }}
      >
        <Typography variant="caption" sx={{ color: "primary.main", width: 14 }}>
          {open ? "▾" : "▸"}
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
          📁 {name}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {count} file{count === 1 ? "" : "s"}
        </Typography>
        {hint && (
          <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
            {hint}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {downloadFiles && downloadFiles.length > 0 && downloadExt && open && (
          <Button
            size="small" variant="text"
            onClick={(e) => {
              e.stopPropagation();
              _downloadAll(downloadExt, downloadFiles);
            }}
            sx={{ textTransform: "none", fontSize: 11, py: 0 }}
          >
            ⬇ Download all
          </Button>
        )}
      </Box>
      {open && <Box>{children}</Box>}
    </Box>
  );
}

// run_python's tool_result is a JSON-encoded string like
//   {"error": "BadRequest 400…\nLocation: US\n…", "stdout": "", "stderr": ""}
// Without parsing, that shows up in the OUTPUT block as literal "\n" escapes
// and `\"` — a wall of unreadable text. Parse it so each component renders
// in its own block with real newlines.
function _parseStatResult(raw) {
  if (!raw) return { ok: true, fields: null };
  // Try JSON first. If it's a wrapped string like `"{\"error\":\"…\"}"` we
  // may need to parse twice (defensive).
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
    if (typeof parsed === "string") {
      try { parsed = JSON.parse(parsed); } catch { /* keep as string */ }
    }
  } catch {
    return { ok: true, fields: null };  // not JSON; render raw
  }
  if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return { ok: true, fields: null };
  }
  const error = typeof parsed.error === "string" ? parsed.error : null;
  const stdout = typeof parsed.stdout === "string" ? parsed.stdout : null;
  const stderr = typeof parsed.stderr === "string" ? parsed.stderr : null;
  if (error == null && stdout == null && stderr == null) {
    return { ok: true, fields: null };  // unknown shape, render raw
  }
  const isError = !!error || (stderr && stderr.trim());
  return { ok: !isError, fields: { error, stdout, stderr } };
}

// One structured section from session.report_sections — the consolidated,
// runnable-by-itself code that produced one part of the report.
function ReportSectionFile({ section }) {
  const langLabel = section.language === "sql" ? "SQL" : "Python";
  return (
    <EvidenceFile
      filename={`${section.id}.${section.language === "sql" ? "sql" : "py"}`}
      narration={section.produces || section.title}
      primaryLabel={langLabel}
      primaryContent={section.code}
    />
  );
}

// "Sections" view of evidence — what the report rests on, deduped from the
// agent's exploration trail. Populated in the background after generate_report
// (see deployment_composer/composition_kickoff.py).
function EvidenceSectionsView({ sections, projectId, onExtracted }) {
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState("");
  const [extractInfo, setExtractInfo] = useState(null); // { count, errors, kept_previous }

  const handleExtract = async () => {
    setExtracting(true);
    setExtractError("");
    setExtractInfo(null);
    try {
      const res = await extractProjectReportSections(projectId);
      // Backend shape:
      //   { sections_count, errors, kept_previous? }
      // kept_previous=true means the run produced 0 valid sections and we
      // didn't overwrite — surface that explicitly so the user knows nothing
      // appeared because nothing valid was extracted, not because of UI lag.
      setExtractInfo({
        count: res?.sections_count ?? 0,
        errors: res?.errors || [],
        keptPrevious: !!res?.kept_previous,
      });
      if (onExtracted) onExtracted(res);
    } catch (e) {
      setExtractError(e?.response?.data?.error || e?.message || "Extract failed");
    } finally {
      setExtracting(false);
    }
  };

  if (!sections || sections.length === 0) {
    return (
      <Box sx={{ py: 4, color: "text.secondary" }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Structured sections aren't ready yet for this report.
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 2 }}>
          New reports get this automatically within ~30 seconds of finishing.
          For an older report you can run it on demand.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={handleExtract}
          disabled={extracting}
          sx={{ textTransform: "none" }}
        >
          {extracting ? "Extracting…" : "Extract sections now"}
        </Button>
        {extractError && (
          <Alert severity="error" sx={{ mt: 1.5 }}>{extractError}</Alert>
        )}
        {extractInfo && extractInfo.count === 0 && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Extraction ran but produced 0 valid sections.
            {extractInfo.errors.length > 0 && (
              <Box component="ul" sx={{ m: 0.5, pl: 2 }}>
                {extractInfo.errors.slice(0, 5).map((err, i) => (
                  <li key={i}><Typography variant="caption">{err}</Typography></li>
                ))}
              </Box>
            )}
            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
              Try again, or check that the report has actual SQL / Python the agent ran.
            </Typography>
          </Alert>
        )}
      </Box>
    );
  }
  return (
    <Stack spacing={1.5}>
      <EvidenceFolder
        name="Report sections"
        hint="The code each section of the report rests on — runnable as-is"
        count={sections.length}
        downloadFiles={sections.map((s) => ({
          name: `${s.id}.${s.language === "sql" ? "sql" : "py"}`,
          content: s.code || "",
        }))}
        downloadExt="zip"
      >
        {sections.map((s) => (
          <ReportSectionFile key={s.id} section={s} />
        ))}
      </EvidenceFolder>
    </Stack>
  );
}

function EvidenceTab({ project, onSectionsExtracted }) {
  const navigate = useNavigate();
  // Treat impersonation as customer-view: while "viewing as <org>" the staff
  // user should see exactly what that org's customer sees. Otherwise the
  // "staff:" links and full failure logs leak through impersonation, which
  // defeats its whole purpose.
  const { isAdmin, isImpersonating } = useImpersonation();
  const showStaffControls = isAdmin && !isImpersonating;
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [view, setView] = useState("sections"); // "sections" | "audit"
  const [loading, setLoading] = useState(!!project.session_id);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!project.session_id) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      getSessionAnalyses(project.session_id).catch((e) => {
        setError(e?.response?.data?.error || e.message || "Failed to load evidence");
        return [];
      }),
      getAnalyzeSession(project.session_id).catch(() => null),
    ])
      .then(([analyses, session]) => {
        setItems(analyses || []);
        setSections(session?.report_sections || []);
      })
      .finally(() => setLoading(false));
  }, [project.session_id]);

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>;
  }

  if (!project.session_id || (items.length === 0 && sections.length === 0 && !error)) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8, color: "text.secondary", gap: 1 }}>
        <Typography variant="body2">No evidence yet — open the chat to start analysis.</Typography>
      </Box>
    );
  }

  if (view === "sections") {
    return (
      <Box>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
        <Box sx={{ mb: 2, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              The code each part of the report rests on — consolidated, runnable
              as-is, and aligned to the section headings you see in the report.
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={() => setView("audit")}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            See audit trail
          </Button>
        </Box>
        <EvidenceSectionsView
          sections={sections}
          projectId={project.project_id || project.id}
          onExtracted={async () => {
            try {
              const sess = await getAnalyzeSession(project.session_id);
              setSections(sess?.report_sections || []);
              if (onSectionsExtracted) onSectionsExtracted();
            } catch {
              // Ignore; manual refresh works
            }
          }}
        />
      </Box>
    );
  }

  // ── Bucket items, slugify filenames once (uniqueness within each folder) ─
  const sqlItems = items.filter((i) => i.type === "sql");
  const allPyItems = items.filter((i) => i.type === "stat");
  const mlItems = items.filter((i) => i.type === "ml");

  // Hide failed Python items from non-admin users. Failed retries are
  // agent-internal mechanics (the agent tried something, got an error,
  // tried again). Customers don't write that code and don't need to see
  // the messy in-betweens — they want the audit of what worked. Staff
  // still see everything for debugging.
  const pyItems = showStaffControls
    ? allPyItems
    : allPyItems.filter((it) => _parseStatResult(it.result).ok);
  const hiddenFailureCount = allPyItems.length - pyItems.length;

  const sqlTaken = new Set();
  const pyTaken = new Set();
  const sqlWithNames = sqlItems.map((it) => ({ ...it, _filename: _filenameFor(it, "sql", sqlTaken) }));
  const pyWithNames = pyItems.map((it) => ({ ...it, _filename: _filenameFor(it, "py", pyTaken) }));

  // Download bundles. For SQL: the raw query. For Python: code only (output is run-time, not source).
  const sqlDownloadFiles = sqlWithNames.map((it) => ({
    name: it._filename, content: it.sql || "", turn: it.turn,
  }));
  const pyDownloadFiles = pyWithNames.map((it) => ({
    name: it._filename, content: it.code || "", turn: it.turn,
  }));

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}

      <Box sx={{ mb: 2, display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
            Audit trail — every step the agent ran during analysis, including
            exploratory queries that didn't end up in the final report.
          </Typography>
        </Box>
        <Button
          size="small"
          onClick={() => setView("sections")}
          sx={{ textTransform: "none", whiteSpace: "nowrap" }}
        >
          Back to sections
        </Button>
      </Box>

      <Stack spacing={1.5}>
        {/* ─── Queries — SQL the agent ran ─────────────────────────── */}
        <EvidenceFolder
          name="Queries"
          hint="BigQuery SQL the agent executed for exploration"
          count={sqlWithNames.length}
          downloadFiles={sqlDownloadFiles}
          downloadExt="sql"
        >
          {sqlWithNames.map((it, i) => (
            <EvidenceFile
              key={i}
              filename={it._filename}
              turn={it.turn}
              narration={it.narration}
              primaryLabel="SQL"
              primaryContent={it.sql}
            />
          ))}
        </EvidenceFolder>

        {/* ─── Analysis — Python snippets the agent ran ────────────── */}
        <EvidenceFolder
          name="Analysis"
          hint={
            hiddenFailureCount > 0
              ? `Python the agent ran · ${hiddenFailureCount} failed retr${hiddenFailureCount === 1 ? "y" : "ies"} hidden`
              : "Python the agent ran (driver effects, statistical models, etc)"
          }
          count={pyWithNames.length}
          downloadFiles={pyDownloadFiles}
          downloadExt="py"
        >
          {pyWithNames.map((it, i) => {
            const { ok, fields } = _parseStatResult(it.result);
            const isFailed = !ok;
            const errorText = fields?.error || null;
            const outputText = fields
              ? [
                  fields.error && `# error\n${fields.error}`,
                  fields.stderr && `# stderr\n${fields.stderr}`,
                  fields.stdout && `# stdout\n${fields.stdout}`,
                ].filter(Boolean).join("\n\n")
              : it.result || "";
            return (
              <EvidenceFile
                key={i}
                filename={it._filename}
                turn={it.turn}
                status={isFailed ? "failed" : "ok"}
                narration={it.narration}
                primaryLabel="Code"
                primaryContent={it.code}
                secondaryLabel={outputText ? "Output" : null}
                secondaryContent={outputText || null}
                failedReason={errorText ? errorText.split("\n")[0].slice(0, 200) : null}
              />
            );
          })}
        </EvidenceFolder>

        {/* ─── Model — the ML run's defining files ─────────────────── */}
        {mlItems.length > 0 && (
          <EvidenceFolder
            name="Model"
            hint="The files that define the trained model"
            count={mlItems.length}
            defaultOpen={true}
          >
            {mlItems.map((it, idx) => (
              <ModelFolderEntry key={idx} item={it} isAdmin={showStaffControls} onView={(id) => navigate(`/runs/${id}`)} />
            ))}
          </EvidenceFolder>
        )}
      </Stack>
    </Box>
  );
}

// One ML run inside the Model folder: name + status badge, then the three
// canonical files (label.sql, features.sql, model.ipynb). Admins get a link
// to /runs/:id for the pipeline-internals debug view; customers don't see it.
// Auth'd notebook download. Plain <a href> doesn't pass the bearer token, so
// the endpoint returned 401. This component fetches the blob through the
// shared api helper (which attaches auth + acting-org headers) and triggers
// a browser save. Spinner + inline error during/after the fetch.
function NotebookDownloadRow({ runId, theme }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const handleClick = async () => {
    setBusy(true);
    setErr("");
    try {
      await downloadV2RunNotebook(runId);
    } catch (e) {
      setErr(e?.message || "Download failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box sx={{
      px: 1.5, py: 0.75,
      borderTop: `1px solid ${theme.palette.divider}`,
      display: "flex", alignItems: "center", gap: 1,
    }}>
      <Typography variant="caption" sx={{ color: "primary.main", width: 14 }}>•</Typography>
      <Box sx={{ fontFamily: "monospace", fontSize: 13, flex: 1 }}>
        model.ipynb
      </Box>
      {err && (
        <Typography variant="caption" color="error" sx={{ mr: 1 }}>
          {err.slice(0, 80)}
        </Typography>
      )}
      <Button
        size="small" variant="text"
        onClick={handleClick}
        disabled={busy}
        startIcon={busy ? <CircularProgress size={12} /> : null}
        sx={{ textTransform: "none", fontSize: 11 }}
      >
        {busy ? "Downloading…" : "⬇ Download"}
      </Button>
    </Box>
  );
}

function ModelFolderEntry({ item, isAdmin, onView }) {
  const theme = useTheme();
  const [open, setOpen] = useState(true);
  const status = item.ml_status || "pending";
  const isSucceeded = status === "succeeded" || status === "complete";
  const isFailed = status === "failed" || status === "error";
  const statusColor = isSucceeded ? "success" : isFailed ? "error" : "default";
  const statusLabel = isSucceeded ? "succeeded" : status;
  const metricStr = (item.metric_name && item.metric_value != null)
    ? `${item.metric_name} ${Number(item.metric_value).toFixed(3)}`
    : null;

  return (
    <Box sx={{ borderTop: `1px solid ${theme.palette.divider}` }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          px: 1.5, py: 0.75,
          display: "flex", alignItems: "center", gap: 1,
          cursor: "pointer", userSelect: "none",
          "&:hover": { bgcolor: theme.palette.action.hover },
        }}
      >
        <Typography variant="caption" sx={{ color: "primary.main", width: 14 }}>
          {open ? "▾" : "▸"}
        </Typography>
        <Box sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 600 }}>
          {_slugify(item.run_name || item.label || "model") || "model"}/
        </Box>
        <Chip label={statusLabel} size="small" color={statusColor} variant="outlined" sx={{ fontSize: 10, height: 18 }} />
        {metricStr && (
          <Chip label={metricStr} size="small" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
        )}
        <Box sx={{ flex: 1 }} />
        {item.turn != null && (
          <Typography variant="caption" color="text.disabled">turn {item.turn}</Typography>
        )}
      </Box>
      {open && (
        <Box sx={{ pl: 3, pb: 1 }}>
          {item.problem_statement && (
            <Typography variant="caption" color="text.secondary" sx={{
              display: "block", fontStyle: "italic", px: 1.5, py: 0.5,
            }}>
              "{item.problem_statement}"
            </Typography>
          )}
          {isFailed && item.error && (
            <Alert severity="error" sx={{ mx: 1.5, my: 0.5, py: 0.5, fontSize: 12 }}>
              {item.error}
            </Alert>
          )}

          {/* The three canonical files */}
          <EvidenceFile
            filename="label.sql"
            primaryLabel="SQL"
            primaryContent={item.label_sql || "(not available — run finished before code-artifact persistence was added)"}
            narration="Defines the model's target column: which rows count as positive vs negative."
          />
          <EvidenceFile
            filename="features.sql"
            primaryLabel="SQL"
            primaryContent={item.features_sql || "(not available — run finished before code-artifact persistence was added)"}
            narration="Defines every feature the model uses. Each block is one feature's materialization SQL."
          />
          {item.has_notebook && item.ml_run_id && (
            <NotebookDownloadRow runId={item.ml_run_id} theme={theme} />
          )}

          {/* Admin-only: deep-dive into the pipeline internals */}
          {isAdmin && item.ml_run_id && (
            <Box sx={{
              px: 1.5, py: 0.5,
              borderTop: `1px solid ${theme.palette.divider}`,
              display: "flex", justifyContent: "flex-end",
            }}>
              <Button
                size="small" variant="text"
                endIcon={<ChevronRightIcon sx={{ fontSize: 14 }} />}
                onClick={() => onView(item.ml_run_id)}
                sx={{ textTransform: "none", fontSize: 11, color: "text.secondary" }}
              >
                staff: pipeline run
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── Composer Live tab (new deployment_composer/) ─────────────────────────────

// Live tab read path:
//   - On mount, read spec.live_artifact.html (cached HTML from the most recent
//     refresh — written by /deployment/render or by the scheduled Cloud Run Job).
//     This is fast (just a Firestore read).
//   - "Refresh now" hits /deployment/render, runs every section, re-renders
//     the HTML via Claude, and writes the result back to spec.live_artifact.
//     The next mount reads the fresh cached html.
function ComposerLiveTab({ projectId, composerSpec, canEdit, onSpecChanged }) {
  const cached = composerSpec?.spec?.live_artifact || {};
  const [html, setHtml] = useState(cached.html || null);
  const [generatedAt, setGeneratedAt] = useState(cached.generated_at || null);
  const [errors, setErrors] = useState(cached.errors || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);

  const shape = composerSpec?.spec?.shape;
  const isUploadDriven = shape === "file_upload_scoring";

  const refresh = () => {
    setLoading(true);
    setError("");
    renderProjectLiveView(projectId)
      .then((res) => {
        setHtml(res?.html || "");
        setGeneratedAt(res?.generated_at || null);
        setErrors(res?.errors || []);
        if (onSpecChanged) onSpecChanged();  // refresh spec to pull updated live_artifact
      })
      .catch((e) => {
        setError(e?.response?.data?.error || e?.message || "Render failed");
      })
      .finally(() => setLoading(false));
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const res = await uploadProjectDeploymentCsv(projectId, file);
      setHtml(res?.html || "");
      setGeneratedAt(res?.generated_at || null);
      setErrors(res?.errors || []);
      if (onSpecChanged) onSpecChanged();
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    // If no cached html yet AND this isn't upload-driven, run the first
    // refresh to populate the cache. Subsequent visits read the cache.
    if (isUploadDriven) return;
    if (!html) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, isUploadDriven]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={refresh}>Retry</Button>
      }>
        {error}
      </Alert>
    );
  }

  return (
    <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {generatedAt
            ? `Refreshed ${new Date(generatedAt).toLocaleString()}`
            : (isUploadDriven ? "Upload a CSV to score" : "—")}
        </Typography>
        {!isUploadDriven && (
          <Button size="small" onClick={refresh} sx={{ textTransform: "none" }}>
            Refresh now
          </Button>
        )}
        {canEdit && (
          <Button
            size="small"
            onClick={() => setRefineOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Refine deployment
          </Button>
        )}
        {isUploadDriven && (
          <Button
            size="small"
            variant="contained"
            component="label"
            disabled={uploading}
            sx={{ textTransform: "none", boxShadow: "none" }}
          >
            {uploading ? "Scoring…" : (html ? "Upload another CSV" : "Upload CSV")}
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";  // allow re-selecting the same file
                if (f) handleUpload(f);
              }}
            />
          </Button>
        )}
      </Box>
      {errors?.length > 0 && (
        <Alert severity="warning">
          {errors.length} section{errors.length === 1 ? "" : "s"} reported errors during render.
        </Alert>
      )}
      {html ? (
        <Box
          sx={{
            width: "100%",
            minWidth: 0,
            overflow: "hidden",
            "& iframe": { width: "100%", border: 0, minHeight: 600 },
          }}
        >
          <iframe
            title="Live view"
            srcDoc={html}
            sandbox="allow-scripts"
          />
        </Box>
      ) : isUploadDriven ? (
        <Alert severity="info">
          This deployment scores each CSV you upload. Use the button above to
          submit a file — predictions will appear here.
        </Alert>
      ) : null}

      <DeploymentReviseDialog
        open={refineOpen}
        projectId={projectId}
        projectName={null}
        onClose={() => setRefineOpen(false)}
        onRevised={() => {
          if (onSpecChanged) onSpecChanged();
          if (!isUploadDriven) refresh();
        }}
      />
    </Stack>
  );
}

// ─── Live tab (legacy) ────────────────────────────────────────────────────────

/**
 * Shared reference data (the CP Wealth stock dashboard) shown above the private
 * prediction panels. Rendered from the pre-built self-contained HTML, so it has
 * NO dependency on the caller's dataset — which is why it sits outside the
 * `liveRunId` guard below and still shows while a sharee's own workspace is
 * provisioning, or for an admin who has no instance at all.
 */
/**
 * Reads the dashboard payload once for the whole Live tab. Both grids and the
 * filter bar need it — the summary/detail rows, the industry list, and the
 * stock->industry map the ranking grid filters through — so it is loaded here
 * rather than inside one of the grids that would then have to publish it upward.
 */
function useStockDashboardData(projectId, html) {
  const [state, setState] = useState({ data: null, error: "" });
  useEffect(() => {
    if (!projectId && !html) { setState({ data: null, error: "" }); return; }
    let live = true;
    setState({ data: null, error: "" });

    /* Preferred path: rows as JSON, read from BigQuery server-side and cached.
       The HTML report is the fallback — it still works, and keeping it means this
       can ship before the endpoint exists and still renders for a project whose
       payload has not been built yet. */
    const load = async () => {
      if (projectId) {
        try {
          const res = await getProjectDashboardData(projectId);
          if (res?.packed) return await readStockPacked(res.packed);
        } catch {
          /* fall through to the report */
        }
      }
      if (html) return await readStockPayload(html);
      throw new Error("No dashboard data available for this project.");
    };

    load()
      .then((data) => { if (live) setState({ data, error: "" }); })
      .catch((e) => {
        if (live) setState({ data: null, error: e?.message || "Could not load the dashboard data." });
      });
    return () => { live = false; };
  }, [projectId, html]);
  return state;
}


function LiveTab({ runsLoading, domainProfile, liveRunId, stockDashboard, projectId }) {
  const [decisionOutputRaw, setDecisionOutputRaw] = useState(null);
  const [decisionLoading, setDecisionLoading] = useState(true);
  const [holdRec, setHoldRec] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  // ONE filter for the page, applied to both grids below.
  const [industry, setIndustry] = useState("");
  const [stock, setStock] = useState("");
  const { data: stockData, error: stockError } = useStockDashboardData(projectId, stockDashboard?.html);

  /* The Stock list spans BOTH grids: every researched stock plus every scored
     security, narrowed by the chosen industry. Listing only the intersection
     would hide the ranked funds and ETFs the research universe has no row for,
     and unreachable data is worse than a grid that says "no rows". */
  const stockOptions = useMemo(() => {
    if (!stockData) return [];
    const seen = new Set();
    for (const src of [stockData.summary, stockData.detail]) {
      for (const r of src.rows) {
        if (industry && r[src.iIdx] !== industry) continue;
        seen.add(r[src.sIdx]);
      }
    }
    for (const [sec, ind] of stockData.indOf.entries()) {
      if (!industry || ind === industry) seen.add(sec);
    }
    return [...seen].sort();
  }, [stockData, industry]);

  // A stock the newly chosen industry doesn't contain would filter everything away.
  useEffect(() => {
    if (stock && stockOptions.length && !stockOptions.includes(stock)) setStock("");
  }, [stockOptions, stock]);

  const dashboardVintage = useMemo(() => {
    const iso = stockDashboard?.dataAsOf || (stockData?.isoDate ? `${stockData.isoDate}T00:00:00` : null);
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    // Time only when we actually have one; a date-only vintage must not imply midnight.
    return stockDashboard?.dataAsOf
      ? d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
      : d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
  }, [stockDashboard, stockData]);

  const filterBar = stockData ? (
    <StockFilterBar
      industries={stockData.industries}
      stockOptions={stockOptions}
      industry={industry}
      stock={stock}
      onIndustry={setIndustry}
      onStock={setStock}
      onClear={() => { setIndustry(""); setStock(""); }}
    />
  ) : null;

  const dashboardSection = stockDashboard ? (
    stockError
      ? <Alert severity="error">{stockError}</Alert>
      : stockData
        ? <StockSummaryGrid data={stockData} industry={industry} stock={stock} vintage={dashboardVintage} />
        : <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={24} /></Box>
  ) : null;

  useEffect(() => {
    if (!liveRunId) { setDecisionLoading(false); return; }
    setDecisionLoading(true);
    getDecisionOutput(liveRunId)
      .then(setDecisionOutputRaw)
      .catch(() => setDecisionOutputRaw(null))
      .finally(() => setDecisionLoading(false));
  }, [liveRunId]);

  if (runsLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} /></Box>;
  }

  const decisionOutput = parseDecisionOutputPayload(decisionOutputRaw);

  // Shared reference data first, then the caller's private prediction workspace.
  // Split this way so the two halves fail independently: no dataset (provisioning,
  // or an admin with no instance) costs you the predictions, not the dashboard.
  if (!liveRunId) {
    return (
      <Stack spacing={3} sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
        {filterBar}
        {dashboardSection}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: stockDashboard ? 4 : 10, color: "text.secondary", gap: 2 }}>
          <Typography variant="body2">No live model configured for this project.</Typography>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ width: "100%", minWidth: 0, maxWidth: "100%" }}>
      {/* Order tells the story: what's out there → which of it scores well →
          check your own trades → the verdict and why to believe it.
          Previously the upload panel and the verdict sat between the two "which
          stocks are good" answers, so the page opened with a form and buried the
          ranked list at the bottom. */}

      {/* 0 · the page's one filter, above everything it drives */}
      {filterBar}

      {/* 1 · the universe you can choose from */}
      {dashboardSection}

      {/* 2 · which of it the model likes. Carries its own legend because it now
             precedes the explanation — `percentile` would otherwise be an
             unexplained column. Native grid rather than RunDataView: it has to
             filter by industry (a column this table lacks, resolved through the
             stock->industry map) and show the percentile as a bar. */}
      {domainProfile.dailyTickerPredictionsTable && (
        <ModelRankingGrid
          runId={liveRunId}
          tableName={domainProfile.dailyTickerPredictionsTable}
          refreshKey={refreshKey}
          industry={industry}
          stock={stock}
          indOf={stockData?.indOf}
        />
      )}

      {/* 3 · your own candidates. Below the answers, because uploading is an
             action you take AFTER seeing what the model likes — and it feeds the
             verdict rendered in (4). */}
      {domainProfile.showBusinessDataSourcePanel && (
        <Box sx={legacySectionSkin}>
        <BusinessDataSourcePanel
          title="Your trades"
          runId={liveRunId}
          onUploadSuccess={() => setRefreshKey((k) => k + 1)}
          onBusinessFrontStaleHoldChange={setHoldRec}
        />
        </Box>
      )}

      {/* 4 · the verdict, its evidence and its caveats — last, so the page leads
             with the answer and keeps the argument for whoever asks why. */}
      {!holdRec && (
        <Box sx={legacySectionSkin}>
        <BusinessRecommendation
          decisionOutput={decisionOutput}
          loading={decisionLoading}
          runId={liveRunId}
          dataRefreshKey={refreshKey}
          domainProfile={domainProfile}
        />
        </Box>
      )}
    </Stack>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // During impersonation, staff should see the customer view verbatim —
  // hide staff-only menu items, debug controls, etc.
  const { isAdmin, isImpersonating } = useImpersonation();
  const showStaffControls = isAdmin && !isImpersonating;
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deploymentInfo, setDeploymentInfo] = useState(null);   // { deployment, effective_run_id, effective_dataset, is_owner, owner_email, owner_display_name, owner_user_id }
  // New-style composer deployment (deployment_composer/). Parallel to the legacy
  // deploymentInfo above. Shape: { spec, deployment_status } | { spec: null }
  const [composerSpec, setComposerSpec] = useState(null);
  const [deployOpen, setDeployOpen] = useState(false);
  // hasReport gates the Deploy CTA. We need an actual generated report on
  // the session to deploy — a session with no report yet shouldn't pretend
  // to be deployable. Lifted to page level so ProjectHeaderStrip and any
  // child can use it without each component re-fetching the session.
  const [hasReport, setHasReport] = useState(false);
  // A renderer-backed report (the CP Wealth stock dashboard) is reference data,
  // not an analysis narrative — it belongs next to the live predictions that use
  // it, not on its own tab. Kept at page level because the same session fetch
  // already runs here for hasReport; the Live tab needs the html and the data
  // vintage, and re-fetching in the child would double the request.
  const [stockDashboard, setStockDashboard] = useState(null); // { html, dataAsOf }

  const refreshComposerSpec = () =>
    getProjectDeploymentSpec(id)
      .then((data) => { setComposerSpec(data?.spec ? data : null); return data; })
      .catch(() => { setComposerSpec(null); return null; });

  useEffect(() => {
    getProject(id)
      .then(async (proj) => {
        setProject(proj);
        // Determine report-readiness from the session — the session is the
        // authoritative source for report_html. Skip if no session linked yet.
        if (proj?.session_id) {
          try {
            const sess = await getAnalyzeSession(proj.session_id);
            setHasReport(!!(sess?.report_ready || sess?.report_html));
            // Only a report produced by a registered renderer moves to Live.
            // `report_html` is now only a FALLBACK for the rows — the grid reads
            // them from the dashboard-data endpoint. Whether the section exists at
            // all is config (domainProfile.stockSummaryDashboard), so deleting the
            // HTML renderer cannot make the dashboard silently disappear. Carried
            // here only so the fallback still has something to read pre-deploy.
            if (sess?.report_html) {
              setStockDashboard({
                html: sess.report_html,
                dataAsOf: sess.report_data_as_of || null,
              });
            }
          } catch {
            setHasReport(false);
          }
        }
      })
      .catch(() => setProject(null))
      .finally(() => setLoading(false));

    listProjectRuns(id)
      .then(setRuns)
      .catch(() => setRuns([]))
      .finally(() => setRunsLoading(false));

    getProjectDeployment(id)
      .then((data) => setDeploymentInfo(data || null))
      .catch(() => setDeploymentInfo(null));

    refreshComposerSpec();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!project) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 10, gap: 2, color: "text.secondary" }}>
        <FolderOutlinedIcon sx={{ fontSize: 48, opacity: 0.3 }} />
        <Typography variant="body2">Project not found.</Typography>
      </Box>
    );
  }

  // Active deployment from the new project-as-deployment model takes precedence over
  // legacy project.config.liveRunId. The backend resolves effective_run_id per caller
  // (owner gets primary; shared user gets their own provisioned run).
  //
  // Sharee guard: if the project HAS a deployment, we MUST use the deployment's
  // resolved run_id (deploymentInfo.effective_run_id). The legacy fallback
  // (`runs.find(succeeded)`) typically returns the OWNER's primary run, so a
  // sharee using that would POST to the owner's run_id and get a 403 from the
  // backend's owner-check on /runs/<id>/user-input-data. The fix: only use
  // legacy when the project itself has no deployment (genuinely legacy projects).
  // For deployment-backed projects, liveRunId is null until deploymentInfo
  // arrives — LiveTab shows "No live model configured" briefly, which is better
  // than a hard 403.
  // Use the existence of the deployment object, not deployment_id — the latter
  // is in the backend's admin-only redaction set, so a sharee or impersonating
  // admin would see deployment_id stripped and the Model/Live tabs would
  // disappear. The deployment object itself survives redaction (only specific
  // platform-internal fields are removed).
  const hasDeployment = !!deploymentInfo?.deployment;
  const projectHasDeployment = !!project?.active_deployment_id;
  const deploymentRunId = deploymentInfo?.effective_run_id || null;
  const legacyLiveRunId = project.config?.liveRunId
    || runs.find((r) => r.status === "succeeded")?.id
    || runs.find((r) => r.status === "succeeded")?.run_id
    || null;
  const liveRunId = deploymentRunId || (projectHasDeployment ? null : legacyLiveRunId);
  const hasComposerSpec = !!composerSpec?.spec;
  const liveConfigured = hasDeployment
    || hasComposerSpec
    || !!(project.config?.domainSlug && project.config.domainSlug !== "default");
  const domainProfile = getDomainProfile(project.config?.domainSlug || "default");

  // UI gating: edit/share = owner or before deployment exists.
  // View deployment details: Assertion ADMINS only (matches backend redaction).
  // Platform internals (datasets, run IDs, etc.) are not for end-users — owners
  // are end-users too. Backend is authoritative; menu hide is cosmetic.
  const currentUserCanEdit = !!(deploymentInfo?.is_owner) || !deploymentInfo;
  const canSeeDetails = !!showStaffControls;

  // Deploy CTA: only the owner; only when a generated report actually exists
  // on the session. A bare session (chat started, no report yet) shouldn't be
  // deployable — there's nothing to schedule. Backend re-validates ownership
  // and the artifact state on click.
  const canDeploy = currentUserCanEdit && hasReport;

  // Tab list:
  //   - "Report"   always (clients see the rendered report read-only)
  //   - "Evidence" only for owners — sharees + clients don't need the audit trail
  //   - "Live"     when a deployment exists or legacy domainSlug configured
  //   - "Ask"      once EITHER a legacy deployment OR a sections-driven
  //                composer spec exists. The BE route picks the right
  //                code path; the legacy `dep.summary` field is now
  //                synthesized from spec fields when only the new shape
  //                is present.
  const askEnabled = hasDeployment || hasComposerSpec;
  // A renderer-backed dashboard moves ONTO Live, so drop the Report tab that
  // would otherwise show the same iframe twice. Only when Live actually exists:
  // a project with no deployment and no domainSlug has no Live tab, and hiding
  // Report there would strand the dashboard with nowhere to render.
  const dashboardOnLive = !!stockDashboard && liveConfigured;
  // Live leads when it hosts the dashboard — `activeTab` defaults to index 0, so
  // leaving Evidence first would land the OWNER on the audit trail instead of the
  // thing they came to look at (a sharee never sees Evidence, so the bug would
  // only have shown up for Yao).
  const tabLabels = dashboardOnLive
    ? [
        "Live",
        ...(currentUserCanEdit ? ["Evidence"] : []),
        ...(askEnabled ? ["Ask"] : []),
      ]
    : [
        "Report",
        ...(currentUserCanEdit ? ["Evidence"] : []),
        ...(liveConfigured ? ["Live"] : []),
        ...(askEnabled ? ["Ask"] : []),
      ];
  const activeTab = Math.min(tab, tabLabels.length - 1);

  return (
    <Box>
      <ProjectHeaderStrip
        project={project}
        deploymentInfo={deploymentInfo}
        onEdit={() => setEditOpen(true)}
        onShare={() => setShareOpen(true)}
        onViewDetails={canSeeDetails ? () => setDetailsOpen(true) : null}
        onDelete={currentUserCanEdit ? async () => {
          await deleteProject(id);
          navigate("/projects");
        } : null}
        onDeploy={canDeploy ? () => setDeployOpen(true) : null}
        canDeploy={canDeploy}
        deployLabel={hasComposerSpec ? "Update deployment" : "Deploy"}
        currentUserCanEdit={currentUserCanEdit}
        canSeeDetails={canSeeDetails}
      />

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 40 }}>
          {tabLabels.map((label) => (
            <Tab key={label} label={label} sx={{ minHeight: 40, textTransform: "none", fontWeight: 500 }} />
          ))}
        </Tabs>
      </Box>

      {tabLabels[activeTab] === "Report" && (
        <ReportTab
          project={project}
          projectId={id}
          canEdit={currentUserCanEdit}
          onProjectUpdated={setProject}
        />
      )}
      {tabLabels[activeTab] === "Evidence" && <EvidenceTab project={project} />}
      {tabLabels[activeTab] === "Live" && (
        hasComposerSpec ? (
          <ComposerLiveTab
            projectId={id}
            composerSpec={composerSpec}
            canEdit={currentUserCanEdit}
            onSpecChanged={refreshComposerSpec}
          />
        ) : (
          <LiveTab
            runsLoading={runsLoading}
            domainProfile={domainProfile}
            liveRunId={liveRunId}
            stockDashboard={stockDashboard}
            projectId={id}
          />
        )
      )}
      {tabLabels[activeTab] === "Ask" && <ModelChat projectId={id} />}

      {editOpen && (
        <EditProjectDialog
          project={project}
          open={editOpen}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => setProject(updated)}
        />
      )}

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        projectId={id}
        orgId={project.org_id}
      />

      <DeploymentDetailsDrawer
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        projectId={id}
      />

      <DeploymentDialog
        open={deployOpen}
        projectId={id}
        projectName={project.name}
        onClose={() => setDeployOpen(false)}
        onDeployed={async () => {
          await refreshComposerSpec();
          // Live always exists after a successful deploy (hasComposerSpec → true).
          // Use a large index; activeTab is clamped by Math.min downstream.
          setTab(99);
        }}
      />
    </Box>
  );
}
